import { Component, Inject, Optional, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AccessLog } from 'src/app/models/access-log.model';
import { AccessLogService } from 'src/app/services/access-log.service';
import { AccessLogAction } from 'src/app/enums/accessLogAction.enum';
import { AccessLogTargetType } from 'src/app/enums/accessLogTargetType.enum';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { AuthService } from 'src/app/services/auth.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { DateFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import { WidgetConfig } from 'src/app/models/dashboard/dashboard-widget.model';
import { WIDGET_CONFIG } from 'src/app/shared/grid/baseGrid.component';

@Component({
  standalone: false,
    selector: 'app-access-log-list',
    templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
    styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [DateFormatPipe],
})
export class AccessLogListComponent extends CoreGridComponent<AccessLog> {

    constructor(
        protected accessLogService: AccessLogService,
        toastService: ToastService,
        commonService: CommonService,
        dialog: MatDialog,
        authService: AuthService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        private translateService: TranslateService,
        private dateFormatPipe: DateFormatPipe,
        @Optional() @Inject(WIDGET_CONFIG) widgetConfig?: WidgetConfig
    ) {
        super(
            toastService,
            dialog,
            commonService,
            authService,
            gridViewService,
            gridSettingsService,
            widgetConfig
        );
        this.gridService = accessLogService;
    }

    protected get objectId(): string {
        return RolePermissionObjects.AccessLog;
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    override initializeProperties(): void {
        this.addEntityTitle = 'Access Log';
        this.sortActive = nameOf<AccessLog>(x => x.timestamp);
        this.defaultOrderBy = 'desc';
        this.searchPlaceholder = 'Search Access Logs';
        this.addDisabled = true;
        this.editDisabled = true;
        this.deleteDisabled = true;
    }

    override initializeColumns(): void {
        this.columnConfigs = [
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.timestamp),
                displayName: 'Timestamp',
                cellTemplate: (row: AccessLog) => row.timestamp ? this.dateFormatPipe.transform(row.timestamp) : '-'
            }),
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.userName),
                displayName: 'User',
                cellTemplate: (row: AccessLog) => row.userName || '-'
            }),
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.accessAction),
                displayName: 'Action',
                cellTemplate: (row: AccessLog) => row.accessAction != null ? this.translateService.instant(this.commonService.getEnumName(AccessLogAction, row.accessAction)) : '-',
                enumFilterMap: this.enumToValueNameArray(AccessLogAction)
            }),
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.targetType),
                displayName: 'Target Type',
                cellTemplate: (row: AccessLog) => row.targetType != null ? this.translateService.instant(this.commonService.getEnumName(AccessLogTargetType, row.targetType)) : '-',
                enumFilterMap: this.enumToValueNameArray(AccessLogTargetType)
            }),
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.targetId),
                displayName: 'Target Id',
                cellTemplate: (row: AccessLog) => row.targetId != null ? row.targetId.toString() : '-'
            }),
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.targetCount),
                displayName: 'Target Count',
                cellTemplate: (row: AccessLog) => row.targetCount != null ? row.targetCount.toString() : '-'
            }),
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.endpoint),
                displayName: 'Endpoint',
                cellTemplate: (row: AccessLog) => row.endpoint || '-'
            }),
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.httpMethod),
                displayName: 'Method',
                cellTemplate: (row: AccessLog) => row.httpMethod || '-'
            }),
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.httpStatusCode),
                displayName: 'Status',
                cellTemplate: (row: AccessLog) => row.httpStatusCode != null ? row.httpStatusCode.toString() : '-'
            }),
            new ColumnConfig<AccessLog>({
                name: nameOf<AccessLog>(x => x.ipAddress),
                displayName: 'IP Address',
                cellTemplate: (row: AccessLog) => row.ipAddress || '-'
            })
        ];
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [];
    }

    override initializeTabs(): { routerLink: string; title: string; }[] {
        return [];
    }

    override getOverviewData(): Observable<void> {
        return of(undefined);
    }

    override openEntityDialog(_entityId?: string): void {
        return;
    }

    override openEntityDeleteDialog(_entity: AccessLog): void {
        return;
    }
}
